import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { Car, Home, Calculator, Loader2, AlertTriangle } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";
import { readRate, activeRuleset } from "@/tax/rulesets";

/**
 * Home Office + Vehicle estimator. Two tabs. Rates come from the
 * versioned ATO knowledge base (`readRate`) so a mid-FY rate change or
 * FY roll-over lands here with zero code edits.
 */
export default function HomeOfficeVehicleCard() {
  const [tab, setTab] = useState("vehicle");
  return (
    <section className="bi-surface p-4 space-y-3" data-testid="home-office-vehicle">
      <div className="flex items-center gap-2 text-[#FF9500]">
        <Calculator className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-heading">
          Deduction Calculators
        </span>
      </div>
      <div className="grid grid-cols-2 gap-1 rounded-xl bi-surface-2 p-1">
        <button
          type="button"
          data-testid="calc-tab-vehicle"
          onClick={() => setTab("vehicle")}
          className={`h-9 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition ${
            tab === "vehicle" ? "bg-[#FF9500] text-white" : "text-white/60"
          }`}
        >
          <Car className="w-3.5 h-3.5" /> Vehicle
        </button>
        <button
          type="button"
          data-testid="calc-tab-home"
          onClick={() => setTab("home")}
          className={`h-9 rounded-lg text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition ${
            tab === "home" ? "bg-[#FF9500] text-white" : "text-white/60"
          }`}
        >
          <Home className="w-3.5 h-3.5" /> Home office
        </button>
      </div>

      {tab === "vehicle" ? <VehicleCalc /> : <HomeOfficeCalc />}

      <TaxDisclaimer compact />
    </section>
  );
}

function VehicleCalc() {
  const [method, setMethod] = useState("cents_per_km");
  const [km, setKm] = useState(3000);
  const [pct, setPct] = useState(60);
  const [rc, setRc] = useState(4000);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Live rate + ruleset for the caption under the km input.
  const centsPerKm = useMemo(() => readRate("centsPerKm"), []);
  const kmLabel = centsPerKm ? `${Math.round(centsPerKm.amount * 100)}c/km` : "91c/km";
  const rulesetVersion = activeRuleset.version;

  const run = async () => {
    setBusy(true);
    try {
      const payload =
        method === "cents_per_km"
          ? { method, km: Number(km) || 0 }
          : { method: "logbook", business_percent: Number(pct) || 0, running_costs: Number(rc) || 0 };
      const { data } = await api.post("/tax/vehicle-calc", payload);
      setResult(data);
    } catch (err) {
      devLog.warn("vehicle calc failed", err);
      toast.error("Calc failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1 rounded-lg bi-surface-2 p-1">
        <button
          type="button"
          data-testid="veh-method-km"
          onClick={() => setMethod("cents_per_km")}
          className={`h-8 rounded-md text-[11px] font-semibold ${
            method === "cents_per_km" ? "bg-white/10 text-white" : "text-white/50"
          }`}
        >
          Cents / km
        </button>
        <button
          type="button"
          data-testid="veh-method-log"
          onClick={() => setMethod("logbook")}
          className={`h-8 rounded-md text-[11px] font-semibold ${
            method === "logbook" ? "bg-white/10 text-white" : "text-white/50"
          }`}
        >
          Logbook
        </button>
      </div>

      {method === "cents_per_km" ? (
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
            Work km this year
          </label>
          <input
            data-testid="veh-km"
            type="number"
            className="bi-input w-full mt-1"
            value={km}
            onChange={(e) => setKm(e.target.value)}
          />
          <div className="text-[11px] text-white/40 mt-1">
            ATO ruleset {rulesetVersion}: {kmLabel} · capped at 5,000km per year
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Business use %
            </label>
            <input
              data-testid="veh-pct"
              type="number"
              className="bi-input w-full mt-1"
              value={pct}
              onChange={(e) => setPct(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Total running $
            </label>
            <input
              data-testid="veh-rc"
              type="number"
              className="bi-input w-full mt-1"
              value={rc}
              onChange={(e) => setRc(e.target.value)}
            />
          </div>
        </div>
      )}

      <button
        type="button"
        data-testid="veh-run"
        onClick={run}
        disabled={busy}
        className="w-full h-11 rounded-xl bi-accent-btn text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}{" "}
        Calculate
      </button>

      {result && (
        <div className="bi-surface-2 p-3 rounded-lg" data-testid="veh-result">
          <div className="text-[10px] uppercase tracking-wider text-white/50">Estimated deduction</div>
          <div className="font-heading font-extrabold text-2xl text-[#34D399] tabular-nums">
            ${Number(result.estimate).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
          </div>
          {result.capped && (
            <div className="mt-1 text-[11px] text-[#FBBF24] inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Capped at 5,000 km (declared: {result.km_declared} km)
            </div>
          )}
          <div className="mt-2 text-[11px] text-white/60 leading-snug">{result.working}</div>
        </div>
      )}
    </div>
  );
}

function HomeOfficeCalc() {
  const [method, setMethod] = useState("fixed_rate");
  const [hpw, setHpw] = useState(15);
  const [weeks, setWeeks] = useState(48);
  const [dedicated, setDedicated] = useState(false);
  const [costs, setCosts] = useState(3000);
  const [pct, setPct] = useState(50);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  // Live WFH rate for the tab-toggle label + "switch to fixed" hint below.
  const wfhFixed = useMemo(() => readRate("wfhFixedRate"), []);
  const wfhLabel = wfhFixed ? `${Math.round(wfhFixed.amount * 100)}c/hour` : "70c/hour";

  const run = async () => {
    setBusy(true);
    try {
      const payload =
        method === "fixed_rate"
          ? { method, hours_per_week: Number(hpw) || 0, weeks: Number(weeks) || 48 }
          : {
              method: "actual",
              has_dedicated_space: dedicated,
              actual_costs: Number(costs) || 0,
              business_use_percent: Number(pct) || 0,
            };
      const { data } = await api.post("/tax/home-office-calc", payload);
      setResult(data);
    } catch (err) {
      devLog.warn("home-office calc failed", err);
      toast.error("Calc failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-1 rounded-lg bi-surface-2 p-1">
        <button
          type="button"
          data-testid="ho-method-fixed"
          onClick={() => setMethod("fixed_rate")}
          className={`h-8 rounded-md text-[11px] font-semibold ${
            method === "fixed_rate" ? "bg-white/10 text-white" : "text-white/50"
          }`}
        >
          Fixed {wfhLabel.replace("/hour", "/hr")}
        </button>
        <button
          type="button"
          data-testid="ho-method-actual"
          onClick={() => setMethod("actual")}
          className={`h-8 rounded-md text-[11px] font-semibold ${
            method === "actual" ? "bg-white/10 text-white" : "text-white/50"
          }`}
        >
          Actual costs
        </button>
      </div>

      {method === "fixed_rate" ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Hours per week
            </label>
            <input
              data-testid="ho-hpw"
              type="number"
              className="bi-input w-full mt-1"
              value={hpw}
              onChange={(e) => setHpw(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
              Weeks (max 52)
            </label>
            <input
              data-testid="ho-weeks"
              type="number"
              className="bi-input w-full mt-1"
              value={weeks}
              onChange={(e) => setWeeks(e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <label
            className="flex items-center gap-2 text-xs cursor-pointer select-none"
            data-testid="ho-dedicated-label"
          >
            <input
              type="checkbox"
              data-testid="ho-dedicated"
              checked={dedicated}
              onChange={(e) => setDedicated(e.target.checked)}
              className="w-4 h-4 accent-[#FF9500]"
            />
            <span className="text-white/80">
              I have a room used <b>only</b> for work
            </span>
          </label>
          {!dedicated && (
            <div className="text-[11px] text-[#FBBF24] inline-flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 mt-0.5" />
              Actual method requires a dedicated workspace. Switch to Fixed {wfhLabel.replace("/hour", "/hr")} if you don&apos;t
              have one.
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
                Yearly running $
              </label>
              <input
                data-testid="ho-costs"
                type="number"
                className="bi-input w-full mt-1"
                value={costs}
                onChange={(e) => setCosts(e.target.value)}
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-heading">
                Work use %
              </label>
              <input
                data-testid="ho-pct"
                type="number"
                className="bi-input w-full mt-1"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        data-testid="ho-run"
        onClick={run}
        disabled={busy}
        className="w-full h-11 rounded-xl bi-accent-btn text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}{" "}
        Calculate
      </button>

      {result && (
        <div className="bi-surface-2 p-3 rounded-lg" data-testid="ho-result">
          <div className="text-[10px] uppercase tracking-wider text-white/50">Estimated deduction</div>
          <div className="font-heading font-extrabold text-2xl text-[#34D399] tabular-nums">
            ${Number(result.estimate).toLocaleString("en-AU", { minimumFractionDigits: 2 })}
          </div>
          {result.eligible === false && (
            <div className="mt-1 text-[11px] text-[#F87171] inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Not eligible under this method
            </div>
          )}
          <div className="mt-2 text-[11px] text-white/60 leading-snug">{result.working}</div>
        </div>
      )}
    </div>
  );
}
